import React, { useState } from 'react'
import styled from '@emotion/styled'
import { EntityState, EntityType, EntityTypeEnum, IssuerOrganisation, ReferenceDataManagementEvent, WorkflowActions } from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Locale, Translation } from 'locale'
import {
  createWorkflowReferenceDataEntitySelector,
  workflowReferenceDataAvailableEventsGetterSelector,
  workflowReferenceDataValidationIssuesGetterSelector
} from 'selectors/workflowSelectors'
import { useSelector, useDispatch } from 'react-redux'
import { useParametricSelector } from 'utils'
import { BooleanSelectboxField } from 'view/shared/entity'
import { issuerLeiFieldLength, issuerOrganisationIdFieldLength } from 'view/styles/constants'
import { IssuerIdTypes, IssuerIdTypeSelectboxField } from 'view/shared/entity/IssuerIdTypeSelectbox'
import { noop } from 'lodash'

interface IssuerOrganisationDialogProps {
  workflowId: string
  issuerOrganisationEntityId: string
  onClose?: () => unknown
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  width: 500px;
  min-height: 150px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
`

const workflowIdSelector = (_state: {}, ownProps: IssuerOrganisationDialogProps) => ownProps.workflowId
const issuerOrganisationEntityIdSelector = (_state: {}, ownProps: IssuerOrganisationDialogProps) => ownProps.issuerOrganisationEntityId
const issuerOrganisationEntitySelector = createWorkflowReferenceDataEntitySelector(
  EntityType.ISSUER_ORGANISATION,
  workflowIdSelector,
  issuerOrganisationEntityIdSelector
)

export const IssuerOrganisationDialog: React.FunctionComponent<IssuerOrganisationDialogProps> = props => {
  const { workflowId, onClose } = props
  const dispatch = useDispatch()
  const issuerOrganisationEntity = useParametricSelector(issuerOrganisationEntitySelector, props)
  const availableEventsSelector = useSelector(workflowReferenceDataAvailableEventsGetterSelector)
  const workflowValidationIssuesSelector = useSelector(workflowReferenceDataValidationIssuesGetterSelector)

  const availableEvents = availableEventsSelector(workflowId)
  const workflowValidationIssues = workflowValidationIssuesSelector(workflowId)
  const [issuerIdType, setIssuerIdType] = useState(IssuerIdTypes.ORGID)

  if (!issuerOrganisationEntity) {
    return null
  }

  const issuerOrganisation = issuerOrganisationEntity.data
  const isNewIssuerOrganisation = issuerOrganisationEntity.entityState === EntityState.NEW
  const title = isNewIssuerOrganisation
    ? Locale.translate(Translation.TC_ADD_ISSUER_ORGANISATION)
    : Locale.translate(Translation.TC_EDIT_ISSUER_ORGANISATION)
  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const validationIssues = issuerOrganisationEntity.validationIssues ?? []
  const hasValidationIssues = validationIssues.length > 0 || workflowValidationIssues.length > 0

  const handleUpdateIssuerOrganisation = <K extends keyof IssuerOrganisation>(key: K) => (value: any) => {
    const partialIssuerOrganisation = { [key]: value }
    dispatch(
      WorkflowActions.createEntityUpdateReferenceData<IssuerOrganisation>({
        entityId: issuerOrganisationEntity.id,
        entityType: EntityTypeEnum.ISSUER_ORGANISATION,
        updatedEntity: {
          ...issuerOrganisation,
          ...partialIssuerOrganisation
        },
        workflowId
      })
    )
  }

  const handleUpdateIssuerIdType = (value: IssuerIdTypes) => {
    if (value === IssuerIdTypes.LEI && isNewIssuerOrganisation) {
      handleUpdateIssuerOrganisation('issuerOrganisationId')(null)
    }
    if (value === IssuerIdTypes.ORGID && isNewIssuerOrganisation) {
      handleUpdateIssuerOrganisation('issuerLei')(null)
    }
    setIssuerIdType(value)
  }

  const handleSave = () => {
    dispatch(WorkflowActions.createSubmitWorkflow(workflowId))
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    dispatch(WorkflowActions.createCancelWorkflow(workflowId))
    if (onClose) {
      onClose()
    }
  }

  return (
    <ModalWindow title={title}>
      <Content>
        {isNewIssuerOrganisation ? (
          <DialogRow>
            <DialogField flex={1}>
              <IssuerIdTypeSelectboxField
                value={issuerIdType}
                onUpdate={handleUpdateIssuerIdType}
                label={Locale.translate(Translation.TC_ISSUER_ID_TYPE)}
                readOnly={!isNewIssuerOrganisation}
              />
            </DialogField>
            {issuerIdType === IssuerIdTypes.ORGID ? (
              <DialogField flex={2}>
                <NumericInputField
                  fieldName="issuerOrganisationId"
                  value={issuerOrganisation.issuerOrganisationId}
                  onUpdate={handleUpdateIssuerOrganisation('issuerOrganisationId')}
                  label={Locale.translate(Translation.TC_ISSUER_ORG_ID)}
                  validationIssues={validationIssues}
                  maxLength={issuerOrganisationIdFieldLength}
                  readOnly={!isNewIssuerOrganisation}
                  formatOptions={{ separator: false }}
                />
              </DialogField>
            ) : (
              <DialogField flex={2}>
                <TextInputField
                  fieldName="issuerLei"
                  value={issuerOrganisation.issuerLei}
                  onUpdate={handleUpdateIssuerOrganisation('issuerLei')}
                  label={Locale.translate(Translation.TC_ISSUER_LEI)}
                  validationIssues={validationIssues}
                  maxLength={issuerLeiFieldLength}
                  readOnly={!isNewIssuerOrganisation}
                  trimInput={true}
                />
              </DialogField>
            )}
          </DialogRow>
        ) : (
          <DialogRow>
            <DialogField>
              <NumericInputField
                fieldName="issuerOrganisationId"
                value={issuerOrganisation.issuerOrganisationId}
                onUpdate={noop}
                label={Locale.translate(Translation.TC_ISSUER_ORG_ID)}
                readOnly={true}
                formatOptions={{ separator: false }}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                fieldName="issuerLei"
                value={issuerOrganisation.issuerLei}
                onUpdate={noop}
                label={Locale.translate(Translation.TC_ISSUER_LEI)}
                readOnly={true}
                trimInput={true}
              />
            </DialogField>
          </DialogRow>
        )}
        <DialogRow>
          <DialogField>
            <BooleanSelectboxField
              fieldName="tradedOnUsExchange"
              value={issuerOrganisation.tradedOnUsExchange}
              onUpdate={handleUpdateIssuerOrganisation('tradedOnUsExchange')}
              label={Locale.translate(Translation.TC_TRADED_ON_US_EXCHANGE)}
              validationIssues={validationIssues}
              canClear={true}
            />
          </DialogField>
          <DialogField>
            <BooleanSelectboxField
              fieldName="sanctionScreened"
              value={issuerOrganisation.sanctionScreened}
              onUpdate={handleUpdateIssuerOrganisation('sanctionScreened')}
              label={Locale.translate(Translation.TC_SANCTIONED_SCREENED)}
              validationIssues={validationIssues}
              canClear={true}
            />
          </DialogField>
          <DialogField>
            <BooleanSelectboxField
              fieldName="complianceSuspended"
              value={issuerOrganisation.complianceSuspended}
              onUpdate={handleUpdateIssuerOrganisation('complianceSuspended')}
              label={Locale.translate(Translation.TC_COMPLIANCE_SUSPENDED)}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton type={TextButtonType.Primary} disabled={readOnly || hasValidationIssues} onClick={handleSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
